'use strict'

import React, { useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import {
  makeStyles,
  Paper,
  Toolbar,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Snackbar
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import AddIcon from '@material-ui/icons/Add'
import PublishIcon from '@material-ui/icons/Publish'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import CompareArrowsIcon from '@material-ui/icons/CompareArrows'
import MapIcon from '@material-ui/icons/Map'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(2)
  },
  paper: {
    width: '100%'
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2)
  },
  titleWrap: {
    minWidth: 320,
    flex: 1
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
  nameField: {
    minWidth: 260
  },
  smallField: {
    minWidth: 120
  },
  cloneSelect: {
    minWidth: 220
  },
  tableWrap: {
    overflowX: 'auto'
  },
  table: {
    minWidth: 1320
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    whiteSpace: 'nowrap'
  },
  actionsCell: {
    minWidth: 360,
    whiteSpace: 'nowrap'
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 2, 2)
  }
}))

const formatDate = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString()
}

const statusColor = (status) => {
  if (status === 'published') return 'primary'
  if (status === 'draft') return 'secondary'
  return 'default'
}

function RoutingScenarioDashboard ({ url }) {
  const classes = useStyles()
  const history = useHistory()

  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [publishingById, setPublishingById] = useState({})
  const [scenarios, setScenarios] = useState([])

  const [scenarioName, setScenarioName] = useState('')
  const [sourceScenarioId, setSourceScenarioId] = useState('')
  const [horizonDays, setHorizonDays] = useState(30)
  const [freshnessThresholdHours, setFreshnessThresholdHours] = useState(6)
  const [proceedWithStaleData, setProceedWithStaleData] = useState(false)

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })

  const draftCount = useMemo(() => scenarios.filter(item => item.status === 'draft').length, [scenarios])
  const publishedCount = useMemo(() => scenarios.filter(item => item.status === 'published').length, [scenarios])
  const archivedCount = useMemo(() => scenarios.filter(item => item.status === 'archived').length, [scenarios])

  const downloadChangePackage = useCallback((changePackage) => {
    if (!changePackage?.csv) return false
    const blob = new Blob([changePackage.csv], { type: changePackage.contentType || 'text/csv;charset=utf-8;' })
    const objectUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = changePackage.fileName || `territory_publish_changes_${Date.now()}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(objectUrl)
    return true
  }, [])

  const fetchScenarios = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios`)
      setScenarios(response.data?.scenarios || [])
    } catch (err) {
      console.error('Error loading territory scenarios:', err)
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to load scenarios',
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [url])

  React.useEffect(() => {
    fetchScenarios()
  }, [fetchScenarios])

  const createScenario = async () => {
    setCreating(true)
    try {
      const payload = {
        name: scenarioName.trim() || undefined,
        sourceScenarioId: sourceScenarioId || undefined,
        horizonDays: Number(horizonDays) || 30,
        freshnessThresholdHours: Number(freshnessThresholdHours) || 6,
        proceedWithStaleData: !!proceedWithStaleData
      }

      const response = await axios.post(`${url}/api/hmi/territory/scenarios`, payload)
      const created = response.data?.scenario

      await fetchScenarios()

      setSnackbar({
        open: true,
        message: created?.isStaleAtCreation
          ? 'Draft scenario created (stale data acknowledged)'
          : 'Draft scenario created',
        severity: 'success'
      })

      if (created?._id) {
        history.push(`/routing/territory?scenarioId=${created._id}`)
      }
    } catch (err) {
      const apiError = err.response?.data
      if (apiError?.requiresFreshnessDecision) {
        setSnackbar({
          open: true,
          message: 'Scenario data is stale. Toggle "Allow stale data" to proceed or run Aspire sync first.',
          severity: 'warning'
        })
      } else {
        console.error('Error creating scenario:', err)
        setSnackbar({
          open: true,
          message: apiError?.message || err.message || 'Failed to create scenario',
          severity: 'error'
        })
      }
    } finally {
      setCreating(false)
    }
  }

  const publishScenario = async (scenario) => {
    if (!scenario?._id || scenario.status !== 'draft') return

    const confirmed = window.confirm(
      `Publish scenario "${scenario.name}"? This will archive the current published scenario and write these changes to live routing data.`
    )
    if (!confirmed) return

    setPublishingById(prev => ({ ...prev, [scenario._id]: true }))
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${scenario._id}/publish`)
      const changePackage = response.data?.changePackage
      const csvDownloaded = downloadChangePackage(changePackage)

      await fetchScenarios()

      setSnackbar({
        open: true,
        message: csvDownloaded
          ? `Published and downloaded CSV (${changePackage?.rowCount || 0} moved properties)`
          : 'Scenario published',
        severity: 'success'
      })
    } catch (err) {
      console.error('Error publishing scenario:', err)
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'Failed to publish scenario',
        severity: 'error'
      })
    } finally {
      setPublishingById(prev => ({ ...prev, [scenario._id]: false }))
    }
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.titleWrap}>
            <Typography variant="h5">Territory Scenario Dashboard</Typography>
            <Typography variant="body2" color="textSecondary">
              Create, review, and publish routing sandbox scenarios.
            </Typography>
            <div className={classes.formRow}>
              <TextField
                className={classes.nameField}
                size="small"
                variant="outlined"
                label="Scenario name"
                value={scenarioName}
                onChange={(event) => setScenarioName(event.target.value)}
                placeholder="Territory Sandbox - Week of ..."
              />
              <Select
                className={classes.cloneSelect}
                size="small"
                variant="outlined"
                value={sourceScenarioId}
                onChange={(event) => setSourceScenarioId(event.target.value)}
                displayEmpty
              >
                <MenuItem value="">Clone from live overrides</MenuItem>
                {scenarios.map(item => (
                  <MenuItem key={item._id} value={item._id}>{item.name}</MenuItem>
                ))}
              </Select>
              <TextField
                className={classes.smallField}
                size="small"
                variant="outlined"
                type="number"
                label="Horizon (days)"
                value={horizonDays}
                onChange={(event) => setHorizonDays(event.target.value)}
                inputProps={{ min: 7, max: 90 }}
              />
              <TextField
                className={classes.smallField}
                size="small"
                variant="outlined"
                type="number"
                label="Freshness (hrs)"
                value={freshnessThresholdHours}
                onChange={(event) => setFreshnessThresholdHours(event.target.value)}
                inputProps={{ min: 1, max: 72 }}
              />
              <FormControlLabel
                control={(
                  <Switch
                    color="secondary"
                    checked={proceedWithStaleData}
                    onChange={(event) => setProceedWithStaleData(event.target.checked)}
                  />
                )}
                label="Allow stale data"
              />
            </div>
          </div>

          <div className={classes.controls}>
            <Button
              variant="outlined"
              color="default"
              startIcon={<RefreshIcon />}
              onClick={fetchScenarios}
              disabled={loading || creating}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={createScenario}
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Scenario'}
            </Button>
            <Button
              variant="outlined"
              color="default"
              startIcon={<ExitToAppIcon />}
              onClick={() => history.push('/routing/territory')}
            >
              Open Workbench
            </Button>
          </div>
        </Toolbar>

        <div className={classes.chipRow}>
          <Chip size="small" label={`Total: ${scenarios.length}`} />
          <Chip size="small" color="secondary" label={`Draft: ${draftCount}`} />
          <Chip size="small" color="primary" label={`Published: ${publishedCount}`} />
          <Chip size="small" label={`Archived: ${archivedCount}`} />
        </div>

        <div className={classes.tableWrap}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Scenario</TableCell>
                <TableCell className={classes.headerCell}>Status</TableCell>
                <TableCell className={classes.headerCell}>Assignments</TableCell>
                <TableCell className={classes.headerCell}>Base Snapshot</TableCell>
                <TableCell className={classes.headerCell}>Routing Sync</TableCell>
                <TableCell className={classes.headerCell}>WorkTicket Sync</TableCell>
                <TableCell className={classes.headerCell}>Created</TableCell>
                <TableCell className={classes.headerCell}>Published</TableCell>
                <TableCell className={classes.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box py={2}><CircularProgress size={24} /></Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading && scenarios.map(item => {
                const isPublishing = !!publishingById[item._id]
                return (
                  <TableRow key={item._id}>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>{item.name}</Typography>
                      <Typography variant="caption" color="textSecondary">{item._id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(item.status)} label={item.status || '-'} />
                    </TableCell>
                    <TableCell>{item.assignmentCount || 0}</TableCell>
                    <TableCell>{formatDate(item.baseSnapshotAt)}</TableCell>
                    <TableCell>{formatDate(item.routingSyncAt)}</TableCell>
                    <TableCell>{formatDate(item.workTicketSyncAt)}</TableCell>
                    <TableCell>{formatDate(item.createdAt)}</TableCell>
                    <TableCell>{formatDate(item.publishedAt)}</TableCell>
                    <TableCell className={classes.actionsCell}>
                      <Box display="flex" gridGap={8}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => history.push(`/routing/territory?scenarioId=${item._id}`)}
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CompareArrowsIcon fontSize="small" />}
                          onClick={() => history.push(`/routing/territory?scenarioId=${item._id}#scenario-compare`)}
                        >
                          Compare
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<MapIcon fontSize="small" />}
                          onClick={() => history.push(`/routing/territory/map?scenarioId=${item._id}`)}
                        >
                          Map
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          startIcon={isPublishing ? <CircularProgress size={14} color="inherit" /> : <PublishIcon fontSize="small" />}
                          onClick={() => publishScenario(item)}
                          disabled={item.status !== 'draft' || isPublishing}
                        >
                          {isPublishing ? 'Publishing...' : 'Publish'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}

              {!loading && scenarios.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="textSecondary">No scenarios found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(RoutingScenarioDashboard)
